import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Box } from '@mui/system'

import { useGetOrderItemsQuery, useUpdateOrderItemsMutation } from '@/app/services/orderItems'
import { useCalculateOrderMutation, useGetOrderQuery, useUpdateOrderMutation } from '@/app/services/orders'
import {
    selectFilteredItemsIds,
    selectFilterOptions,
    selectIsUpdateQueueEmpty,
    selectOpenDrawingDoctorModal,
    selectOpenPartOrderHistoryModal,
    selectSelectedPartFromLibrary,
    setFilteredItemsIds,
    setFilterOptions,
    setIsCalculatingOrder,
    setOpenDrawingDoctorModal,
    setOpenPartOrderHistoryModal,
    setSelectedOrderItemDrawingId,
    setSelectedOrderStatus,
    setSelectedPartFromLibrary,
} from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { ORDER_STATUS_ARRAY } from '@/common/utils'
import {
    orderItemErrors,
    orderItemHasIssues,
    orderItemUnset,
    orderItemWarnings,
} from '@/common/utils/OrderUtils/OrderUtilities'
import DrawingDoctorModal from '@/features/drawing-doctor/DrawingDoctorModal/DrawingDoctorModal'

import OrderItem from './ItemContent/OrderItem'
import OrderItemOverlay from './OrderItemOverlay'
import SeekOrderItemOverlay from './SeekOrderItemOverlay'

// Defined once, outside the component: a component type created during render is a new type on
// every render, so React would remount the whole list (and lose each card's state) on each refresh.
const VirtuosoList = ({ ref, ...props }) => (
    <Box
        {...props}
        ref={ref}
        sx={[
            {
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            },
            ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ]}
    ></Box>
)

const virtuosoComponents = { List: VirtuosoList, ScrollSeekPlaceholder: SeekOrderItemOverlay }

const OrderItemList = () => {
    const { t } = useTranslation()
    const { showMutationError } = useMutationErrorSnackbar()
    const dispatch = useDispatch()
    const { orderId } = useParams()

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const organisationId = useSelector(selectOrganisationId)
    const isDrawingDoctorModalOpen = useSelector(selectOpenDrawingDoctorModal)
    const filterOptions = useSelector(selectFilterOptions)
    const orderItemsIds = useSelector(selectFilteredItemsIds)
    const isPartOrderHistoryModalOpen = useSelector(selectOpenPartOrderHistoryModal)
    const selectedPartFromLibrary = useSelector(selectSelectedPartFromLibrary)
    const isUpdateQueueEmpty = useSelector(selectIsUpdateQueueEmpty)

    const { data: selectedOrder } = useGetOrderQuery({ organisationId, orderId })
    const { data: orderItems, isLoading: isLoadingOrderitems } = useGetOrderItemsQuery({ organisationId, orderId })

    const [calculateOrderMutation, { isLoading: isCalculating }] = useCalculateOrderMutation({
        fixedCacheKey: 'shared-calculate-order',
    })

    const [updateOrderItems] = useUpdateOrderItemsMutation()
    const [updateOrder] = useUpdateOrderMutation()

    const [activeItemId, setActiveItemId] = useState(null)
    const [activeItemName, setActiveItemName] = useState(null)

    const onDragStart = (event) => {
        const id = event.active.id
        setActiveItemId(id)

        const activeItem = orderItems.find((item) => item?.id === id)
        setActiveItemName(activeItem.name)
    }

    const handleUpdateOrder = async (updatedFields) => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    ...selectedOrder,
                    ...updatedFields,
                },
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('$t(An error occurred) updating the order.'))
        }
    }

    const handleUpdateOrderItems = async (sortedList) => {
        try {
            await updateOrderItems({
                organisationId,
                orderId,
                orderItems: sortedList,
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('Failed to reorder order items. Undoing changes'))
        } finally {
            setActiveItemId(null)
        }
    }

    const handleDrawingCleanerClose = () => {
        dispatch(setOpenDrawingDoctorModal(false))
        dispatch(setSelectedOrderItemDrawingId(null))
        dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
            handleUpdateOrder({ status: ORDER_STATUS_ARRAY.NotCalculated })
        }
    }

    const handleClosePartOrderHistoryModal = () => {
        dispatch(setOpenPartOrderHistoryModal(false))
        dispatch(setSelectedPartFromLibrary(null))
    }

    const onDragEnd = useCallback(
        (event) => {
            const { active, over } = event

            if (!active || !over) {
                return
            }

            if (active.id === over.id) {
                return
            }

            const oldIndex =
                active?.data?.current?.sortable?.index ?? orderItems.find((item) => item?.id === active.id)?.index
            const newIndex = over.data.current.sortable.index

            const items = structuredClone(arrayMove(orderItems, oldIndex, newIndex))

            items?.forEach((item, index) => {
                item.index = index
            })

            if (selectedOrder.sortOrderItemsBy !== 'Custom') {
                handleUpdateOrder({ sortOrderItemsBy: 'Custom', sortOrderItemsAsc: true })
            }
            handleUpdateOrderItems(items)
        },
        [orderItems, updateOrderItems, orderId, showMutationError, t]
    )

    const filteredOrderItems = useMemo(() => {
        if (filterOptions === 'showAllItems') {
            return orderItems
        }

        return orderItems.filter((orderItem) => {
            if (filterOptions.includes('showUnsetItems') && orderItemUnset(orderItem)) {
                return true
            }

            if (
                filterOptions.includes('showWarningItems') &&
                !orderItemUnset(orderItem) &&
                orderItemWarnings(orderItem)
            ) {
                return true
            }

            if (filterOptions.includes('showErrorItems') && !orderItemUnset(orderItem) && orderItemErrors(orderItem)) {
                return true
            }

            if (
                filterOptions.includes('showSuccessItems') &&
                !orderItemHasIssues(orderItem) &&
                !orderItemUnset(orderItem)
            ) {
                return true
            }

            return false
        })
    }, [orderItems, filterOptions])

    const calculateOrder = async () => {
        try {
            await calculateOrderMutation({ organisationId, orderId }).unwrap()
        } catch (error) {
            showMutationError(error, t(error.data))
        }
    }

    useEffect(() => {
        if (!filterOptions.includes('showAllItems') && filteredOrderItems.length <= 0) {
            dispatch(setFilterOptions('showAllItems'))
        }

        dispatch(setFilteredItemsIds(filteredOrderItems.map((item) => item.id)))
    }, [filteredOrderItems, filterOptions])

    useEffect(() => {
        dispatch(setIsCalculatingOrder(isCalculating))
    }, [isCalculating])

    useEffect(() => {
        if (
            organisationId &&
            orderId &&
            [ORDER_STATUS_ARRAY.Draft].includes(selectedOrder?.status) &&
            isUpdateQueueEmpty
        ) {
            calculateOrder()
        }
    }, [organisationId, orderId, selectedOrder?.status, isUpdateQueueEmpty])

    return !isLoadingOrderitems ? (
        <>
            <DndContext
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                sensors={sensors}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
            >
                <SortableContext
                    items={orderItemsIds}
                    strategy={verticalListSortingStrategy}
                >
                    <Virtuoso
                        components={virtuosoComponents}
                        data={filteredOrderItems}
                        defaultItemHeight={510}
                        increaseViewportBy={500}
                        itemContent={(index, orderItem) => (
                            <OrderItem
                                id={orderItem?.id}
                                key={orderItem?.id}
                            />
                        )}
                        scrollSeekConfiguration={{
                            enter: (velocity) => Math.abs(velocity) > 600,
                            exit: (velocity) => Math.abs(velocity) < 100,
                        }}
                        style={{ height: '100%' }}
                        useWindowScroll
                    />
                </SortableContext>
                <DragOverlay>
                    {activeItemId ? (
                        <OrderItemOverlay
                            id={activeItemId}
                            name={activeItemName}
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>

            {isDrawingDoctorModalOpen ? (
                <DrawingDoctorModal
                    open={isDrawingDoctorModalOpen}
                    onClose={handleDrawingCleanerClose}
                />
            ) : null}
        </>
    ) : null
}

OrderItemList.propTypes = {}

export default memo(OrderItemList)
